import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import {
  DEFAULT_DROPDOWN_STYLE_VARIANT,
  getDropdownState,
  getDropdownStyleVariantValues,
  TDropdownDataItem,
  TDropdownStyleVariantsKey,
} from 'shared/design-system/theme/dropdowns';
import { generateRandomString } from 'shared/utils';
import { handleFocusedKeyPress } from 'shared/utils/handleFocusedKeypress';
import { ArrowUpIcon } from '../../icons/ArrowUpIcon';
import {
  CustomSelectContainer,
  SelectSelected,
  NativeSelect,
  SelectScrim,
  InputHint,
  MultiSelectDisplay,
  DisplayText,
} from './Select.styles';
import { ArrowDownIcon } from '../../icons/ArrowDownIcon';
import { Dropdown } from '../../Dropdown/Dropdown';
import { DropdownItem } from '../../Dropdown/DropdownItem/DropdownItem';
import { InputLabel } from '../TextInput/TextInput.styles';
import { TDropdownTargetPosition } from '../../Dropdown/Dropdown.styles';

type TSelectProps = {
  id?: string;
  ariaLabelledBy?: string;
  role?: string;
  label?: string;
  placeholder?: string;
  value?: string;
  isDisabled?: boolean;
  defaultOption?: TDropdownDataItem;
  options: TDropdownDataItem[];
  styleVariant?: TDropdownStyleVariantsKey;
  onSelectOption?: (value: string) => void;
  className?: string;
  borderRadius?: boolean;
  marginTop?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  frontDropDown?: boolean;
  // Multi-select props
  multiSelect?: boolean;
  selectedValues?: TDropdownDataItem['value'][];
  onSelectMultiple?: (values: string[]) => void;
};

/**
 * @param {string} [id] - id (optional)
 * @param {string} [ariaLabelledBy] - aria-labelledby (optional)
 * @param {string} [role] - role of dropdown container. defaults to listbox (optional)
 * @param {string} [label] - label of dropdown container. (optional)
 * @param {string} [placeholder] - placeholder when no item is selected (optional)
 * @param {string} [value] - value for select (optional)
 * @param {boolean} [isDisabled] - is the select disabled (optional)
 * @param {TDropdownDataItem} [defaultOption] - default option to select (optional)
 * @param {TDropdownDataItem[]} options - select options (required)
 * @param {TDropdownStyleVariantsKey} [styleVariant] - style variant for theming (optional)
 * @param {function} [onSelectOption] - single select click handler (value: string) => void (optional)
 * @param {string} [className] - required to expose the overall component to styled-components (optional)
 * @param {boolean} [borderRadius] - if true, make border radius smaller (optional)
 * @param {boolean} [marginTop] - if true, increase the margin between text and the selector (optional)
 * @param {boolean} [hasError] - if true, error message is shown (optional)
 * @param {string} [errorMessage] - error message to display (optional)
 * @param {boolean} [frontDropDown] - if true Drop Down goes above the container (optional)
 * @param {boolean} [multiSelect] - Enable multi-select mode with checkboxes (optional)
 * @param {TDropdownDataItem['value'][]} [selectedValues] - Array of selected values for controlled multi-select (optional)
 * @param {function} [onSelectMultiple] - Multi-select change handler (values: string[]) => void (optional)
 */
export function Select({
  id,
  ariaLabelledBy,
  defaultOption,
  role = 'listbox',
  label,
  placeholder = 'Select an option',
  value,
  isDisabled = false,
  options,
  styleVariant = DEFAULT_DROPDOWN_STYLE_VARIANT,
  onSelectOption,
  className,
  borderRadius,
  marginTop,
  hasError,
  errorMessage,
  frontDropDown,
  // Multi-select props
  multiSelect = false,
  selectedValues = [],
  onSelectMultiple,
}: TSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<TDropdownTargetPosition | undefined>(
    undefined,
  );
  // Single select state
  const [selectedOption, setSelectedOption] = useState<TDropdownDataItem>();
  // Multi-select state
  const [selectedOptions, setSelectedOptions] = useState<TDropdownDataItem[]>([]);

  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const displayTextRef = useRef<HTMLDivElement | null>(null);

  const inputId = useMemo(() => id || generateRandomString(), [id]);
  const showErrorState = hasError || Boolean(errorMessage);
  const shouldShowHint = showErrorState;

  const dropdownState = getDropdownState(isDisabled, showErrorState);
  const { fontColor } = getDropdownStyleVariantValues(theme, styleVariant, dropdownState);

  // Initialize selected options for single select
  useEffect(() => {
    if (!multiSelect) {
      let newOption = options.find((option) => option.value === value);
      newOption = newOption || defaultOption;
      if (newOption) setSelectedOption(newOption);
    }
  }, [options, value, defaultOption, multiSelect]);

  // Initialize selected options for multi-select
  useEffect(() => {
    if (multiSelect && selectedValues) {
      const newSelectedOptions = selectedValues
        .map((val) => options.find((option) => option.value === val))
        .filter(Boolean) as TDropdownDataItem[];
      setSelectedOptions(newSelectedOptions);
    }
  }, [multiSelect, selectedValues, options]);

  useEffect(() => {
    if (isClosing) setIsClosing(false);
  }, [isClosing]);

  const handleOpenDropdown = () => {
    if (!isClosing && !isDisabled) {
      const rect = containerRef.current?.getBoundingClientRect();
      setDropdownPosition(rect);
      setIsOpen(true);
    }
    setIsClosing(false);
  };

  const handleCloseDropdown = () => {
    setIsClosing(true);
    setIsOpen(false);
  };

  const handleOptionClick = (option: TDropdownDataItem) => {
    if (multiSelect) {
      const isAlreadySelected = selectedOptions.some((opt) => opt.value === option.value);
      let newSelection: TDropdownDataItem[];

      if (isAlreadySelected) {
        // Remove if already selected
        newSelection = selectedOptions.filter((opt) => opt.value !== option.value);
      } else {
        newSelection = [...selectedOptions, option];
      }

      setSelectedOptions(newSelection);
      onSelectMultiple?.(newSelection.map((opt) => opt.value || ''));
      // Keep dropdown open for multi-select
    } else {
      // single-select logic
      onSelectOption?.(option.value || '');
      setSelectedOption(option);
      setIsOpen(false);
    }
  };

  const handleDropdownKeyPress = (e: React.KeyboardEvent<HTMLElement>) => {
    if (isDisabled) return;
    handleFocusedKeyPress(e, () => {
      const rect = containerRef.current?.getBoundingClientRect();
      setDropdownPosition(rect);
      setIsOpen(!isOpen);
    });
  };

  const handleItemKeyPress = (e: React.KeyboardEvent<HTMLElement>, option: TDropdownDataItem) => {
    handleFocusedKeyPress(e, () => handleOptionClick(option));
  };

  const getDisplayText = () => {
    if (multiSelect) {
      if (selectedOptions.length === 0) return placeholder;

      const titles = selectedOptions.map((opt) => opt.title);
      const fullText = titles.join(', ');

      return fullText;
    }
    return selectedOption?.title || placeholder;
  };

  const isOptionSelected = (option: TDropdownDataItem) => {
    if (multiSelect) {
      return selectedOptions.some((opt) => opt.value === option.value);
    }
    return selectedOption?.value === option.value;
  };

  return (
    <CustomSelectContainer
      ref={containerRef}
      id={id}
      aria-labelledby={ariaLabelledBy}
      role={role}
      className={className}
      onKeyDown={handleDropdownKeyPress}
    >
      {label && (
        <InputLabel
          id={`${inputId}-select-title`}
          htmlFor={`${inputId}-TextInput`}
          $styleVariant="default"
          $inputState="enabled"
        >
          {label}
        </InputLabel>
      )}

      <SelectScrim $show={isOpen} />

      <NativeSelect
        id={`${inputId}-TextInput`}
        value={multiSelect ? selectedOptions.map((opt) => opt.value) : selectedOption?.value}
        multiple={multiSelect}
        onChange={(e) => {
          if (multiSelect) {
            const values = Array.from(e.target.selectedOptions).map((option) => option.value);
            onSelectMultiple?.(values);
          } else {
            onSelectOption?.(e.target.value);
          }
        }}
      />

      <SelectSelected
        $styleVariant={styleVariant}
        $state={dropdownState}
        onClick={handleOpenDropdown}
        tabIndex={isDisabled ? -1 : 0}
        $borderRadius={borderRadius}
        $marginTop={marginTop}
      >
        <MultiSelectDisplay ref={displayTextRef} title={getDisplayText()}>
          <DisplayText as="h5" color={fontColor} $hideOverflow={multiSelect}>
            {getDisplayText()}
          </DisplayText>
        </MultiSelectDisplay>

        {isOpen ? (
          <ArrowUpIcon color={theme.colors.iconIconPrimaryPressedSecondary2100} />
        ) : (
          <ArrowDownIcon color={theme.colors.iconIconPrimaryPressedSecondary2100} />
        )}
      </SelectSelected>

      <Dropdown
        styleVariant={styleVariant}
        target={dropdownPosition}
        isOpen={isOpen}
        onClickOutside={handleCloseDropdown}
        useCreatePortal={frontDropDown}
        isFullWidth
      >
        {options.map((option) => (
          <DropdownItem
            styleVariant={styleVariant}
            key={option.title}
            onClick={() => handleOptionClick(option)}
            onKeyDown={(e) => handleItemKeyPress(e, option)}
            dropdownItemData={option}
            $isSelected={isOptionSelected(option)}
            $isMultiSelect={multiSelect}
          />
        ))}
      </Dropdown>

      <InputHint
        $styleVariant={styleVariant}
        $inputState={dropdownState}
        $showHint={shouldShowHint}
      >
        {errorMessage}
      </InputHint>
    </CustomSelectContainer>
  );
}

export default Select;
